import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewChild, Input, Output, NgModule } from '@angular/core';
import * as i6 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@angular/material/chips';
import { MatChipsModule } from '@angular/material/chips';
import * as i3 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i4 from '@angular/material/autocomplete';
import { MatAutocomplete, MatAutocompleteTrigger, MatAutocompleteModule } from '@angular/material/autocomplete';
import * as i1 from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { ENTER, COMMA } from '@angular/cdk/keycodes';
import { distinctUntilChanged, takeUntil, map, delay } from 'rxjs/operators';
import { Subject, merge } from 'rxjs';
import * as i5 from '@angular/material/core';

class AiHubPickerComponent {
    constructor(formBuilder) {
        this.seperatorKeyCodes = [ENTER, COMMA];
        /**
         * select strategy for the items, could be list for a list of items
         * or autocomplete
         */
        this.itemSelectStrategy = 'list';
        this.isDirty = false;
        this.isAutocompleteSelection = false;
        this.available$ = new Subject();
        this.destroy$ = new Subject();
        this.availableItems = [];
        this.selectedItems = [];
        /**
         * chache to add currently used alliances so they could not
         * selected twice
         */
        this.usedItems = [];
        this.itemsChanged = new EventEmitter();
        this.chipInputCtrl = formBuilder.control(null);
    }
    set available(items) {
        this.availableItems = [...items];
        this.isDirty = true;
    }
    get available() {
        return this.availableItems;
    }
    set values(values) {
        this.selectedItems = [...values];
        this.isDirty = true;
    }
    get values() {
        return this.selectedItems;
    }
    /**
     * add new alliance
     */
    addItem(event) {
        if (this.isAutocompleteSelection) {
            return;
        }
        const value = event.value;
        /** trim value */
        if ((event.value || '').trim()) {
            this.addItemToList(value);
        }
        event.input.value = '';
    }
    /**
     * remove an item
     */
    removeItem(source) {
        var _a;
        const valueIndex = this.values.findIndex((item) => item.toLowerCase() === source.toLowerCase());
        const usedIndex = this.usedItems.findIndex((item) => item.toLocaleLowerCase() === source.toLowerCase());
        this.values.splice(valueIndex, 1);
        if (usedIndex > -1) {
            /** remove item from used list and push back again to available items */
            this.available.push(this.usedItems.splice(usedIndex, 1)[0]);
            this.available$.next();
        }
        this.sendUpdate();
        (_a = this.autocompleteTrigger) === null || _a === void 0 ? void 0 : _a.closePanel();
    }
    /**
     * component gets intialized
     */
    ngOnInit() {
        /**
         * register on chipInputCtrl change only for autocomplete
         */
        if (this.itemSelectStrategy === 'autocomplete') {
            const inputChange$ = this.chipInputCtrl.valueChanges
                .pipe(distinctUntilChanged());
            this.filteredItems = merge(inputChange$, this.available$)
                .pipe(takeUntil(this.destroy$), map(() => this.filter().sort()), delay(0));
        }
        this.validateChips();
    }
    ngAfterViewInit() {
        var _a;
        (_a = this.autocomplete) === null || _a === void 0 ? void 0 : _a.optionActivated.pipe(takeUntil(this.destroy$)).subscribe((event) => this.isAutocompleteSelection = !!event.option);
    }
    ngAfterViewChecked() {
        if (this.isDirty) {
            this.validateChips();
            this.available$.next();
        }
    }
    /**
     * component gets destroyed
     */
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.complete();
        this.destroy$ = null;
    }
    /**
     * select allianz from list
     */
    selectItem(value) {
        this.addItemToList(value);
        this.chipInput.nativeElement.value = '';
        this.chipInputCtrl.setValue(null);
    }
    /**
     * adds a chip to selection
     */
    addItemToList(value) {
        var _a;
        if (this.values.some((item) => item.toLowerCase() === value.toLowerCase())) {
            return;
        }
        this.values.push(value);
        /** check given input is in available */
        const isAvailable = this.available.findIndex((item) => item.toLowerCase() === value.toLowerCase());
        if (isAvailable > -1) {
            /** remove item from available and push it to used items */
            this.usedItems.push(this.available.splice(isAvailable, 1)[0]);
            this.available$.next();
        }
        (_a = this.autocompleteTrigger) === null || _a === void 0 ? void 0 : _a.closePanel();
        this.sendUpdate();
    }
    /**
     * list has been changed
     */
    sendUpdate() {
        this.itemsChanged.emit([...this.values]);
    }
    /**
     * filter items for autocomplete only
     */
    filter() {
        var _a, _b;
        const filterValue = (_b = (_a = this.chipInputCtrl.value) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '';
        return this.available.filter((item) => item.toLowerCase().indexOf(filterValue) === 0);
    }
    /**
     * validate chips
     */
    validateChips() {
        this.usedItems = [];
        for (let i = this.available.length - 1; i >= 0; i--) {
            const value = this.available[i];
            const isSelected = this.values.some((source) => source === value);
            if (isSelected) {
                this.usedItems.push(this.available.splice(i, 1)[0]);
            }
        }
        this.isDirty = false;
    }
}
AiHubPickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubPickerComponent, deps: [{ token: i1.FormBuilder }], target: i0.ɵɵFactoryTarget.Component });
AiHubPickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: AiHubPickerComponent, selector: "aihub-picker", inputs: { available: "available", values: "values", itemSelectStrategy: "itemSelectStrategy" }, outputs: { itemsChanged: "itemsChanged" }, viewQueries: [{ propertyName: "autocomplete", first: true, predicate: MatAutocomplete, descendants: true }, { propertyName: "autocompleteTrigger", first: true, predicate: MatAutocompleteTrigger, descendants: true }, { propertyName: "chipInput", first: true, predicate: ["chipInput"], descendants: true }], ngImport: i0, template: "<!-- \r\n    pills selector with list select strategy, selectable items will show below as selectable list\r\n-->\r\n<ng-container *ngIf=\"itemSelectStrategy==='list'\">\r\n    <label class=\"item-form-control\">\r\n        <mat-chip-list #chipList [selectable]=\"false\">\r\n\r\n            <mat-chip *ngFor=\"let value of values\" [removable]=\"true\" (removed)=\"removeItem(value)\">\r\n                {{value}} <mat-icon matChipRemove>cancel</mat-icon>\r\n            </mat-chip>\r\n\r\n            <!-- \r\n                input field without autocomplete\r\n            -->\r\n            <input\r\n                #chipInput\r\n                [matChipInputFor]=\"chipList\"\r\n                [matChipInputSeparatorKeyCodes]=\"seperatorKeyCodes\"\r\n                [matChipInputAddOnBlur]=\"true\"\r\n                (matChipInputTokenEnd)=\"addItem($event)\"\r\n                autocomplete=\"off\">\r\n\r\n        </mat-chip-list>\r\n    </label>\r\n\r\n    <mat-chip-list #itemList *ngIf=\"available.length\" class=\"available-items\">\r\n        <mat-chip *ngFor=\"let item of available\" (click)=\"selectItem(item)\">\r\n            {{item}}\r\n        </mat-chip>\r\n    </mat-chip-list>\r\n</ng-container>\r\n\r\n<!--\r\n    pills selector with autocomplete strategy, we have to add matAutocompleteTrigger to the input field\r\n    and yes it is possible to do it dynamically. But then we require an instance of MatAutocompleteTrigger\r\n    and this one requires 10 constructor parameters, this is why this exists again here.\r\n-->\r\n<ng-container *ngIf=\"itemSelectStrategy==='autocomplete'\">\r\n    <label class=\"item-form-control\" matAutocompleteOrigin #origin=\"matAutocompleteOrigin\">\r\n        <mat-chip-list #chipList [selectable]=\"false\">\r\n\r\n            <mat-chip *ngFor=\"let value of values\" [removable]=\"true\" (removed)=\"removeItem(value)\">\r\n                {{value}} <mat-icon matChipRemove>cancel</mat-icon>\r\n            </mat-chip>\r\n\r\n            <!-- \r\n                input field without autocomplete\r\n            -->\r\n            <input\r\n                #chipInput\r\n                [formControl]=\"chipInputCtrl\"\r\n                [matAutocomplete]=\"itemAutocomplete\"\r\n                [matAutocompleteConnectedTo]=\"origin\"\r\n                [matChipInputFor]=\"chipList\"\r\n                [matChipInputSeparatorKeyCodes]=\"seperatorKeyCodes\"\r\n                (matChipInputTokenEnd)=\"addItem($event)\"\r\n                autocomplete=\"off\">\r\n        </mat-chip-list>\r\n    </label>\r\n\r\n    <mat-autocomplete #itemAutocomplete=\"matAutocomplete\" (optionSelected)=\"selectItem($event.option.viewValue)\">\r\n        <mat-option *ngFor=\"let item of filteredItems | async\" [value]=\"item\">\r\n            <span>{{item}}</span>\r\n        </mat-option>\r\n    </mat-autocomplete>\r\n</ng-container>\r\n", styles: [":host{display:block;margin-bottom:3rem}:host .item-form-control{border:1px solid #e9e9e9;border-radius:2px;padding:13px 18px;width:100%;display:block;box-shadow:0 5px 5px #e4e4e6}:host ::ng-deep .mat-chip-list-wrapper input{border:0;outline:none}:host ::ng-deep .mat-chip-list-wrapper .mat-chip{background:#86bc25;color:#fff}:host ::ng-deep .mat-chip-list-wrapper .mat-chip-remove{color:#fff;opacity:1}:host .available-items{display:block;margin-top:1.5rem}:host .available-items ::ng-deep .mat-chip{background:white;border:1px solid #86bc25;color:#86bc25}\n"], components: [{ type: i2.MatChipList, selector: "mat-chip-list", inputs: ["errorStateMatcher", "multiple", "compareWith", "value", "required", "placeholder", "disabled", "aria-orientation", "selectable", "tabIndex"], outputs: ["change", "valueChange"], exportAs: ["matChipList"] }, { type: i3.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { type: i4.MatAutocomplete, selector: "mat-autocomplete", inputs: ["disableRipple"], exportAs: ["matAutocomplete"] }, { type: i5.MatOption, selector: "mat-option", exportAs: ["matOption"] }], directives: [{ type: i6.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i6.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.MatChip, selector: "mat-basic-chip, [mat-basic-chip], mat-chip, [mat-chip]", inputs: ["color", "disableRipple", "tabIndex", "selected", "value", "selectable", "disabled", "removable"], outputs: ["selectionChange", "destroyed", "removed"], exportAs: ["matChip"] }, { type: i2.MatChipRemove, selector: "[matChipRemove]" }, { type: i2.MatChipInput, selector: "input[matChipInputFor]", inputs: ["matChipInputFor", "matChipInputAddOnBlur", "matChipInputSeparatorKeyCodes", "placeholder", "id", "disabled"], outputs: ["matChipInputTokenEnd"], exportAs: ["matChipInput", "matChipInputFor"] }, { type: i4.MatAutocompleteOrigin, selector: "[matAutocompleteOrigin]", exportAs: ["matAutocompleteOrigin"] }, { type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { type: i4.MatAutocompleteTrigger, selector: "input[matAutocomplete], textarea[matAutocomplete]", exportAs: ["matAutocompleteTrigger"] }, { type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }], pipes: { "async": i6.AsyncPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubPickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aihub-picker', template: "<!-- \r\n    pills selector with list select strategy, selectable items will show below as selectable list\r\n-->\r\n<ng-container *ngIf=\"itemSelectStrategy==='list'\">\r\n    <label class=\"item-form-control\">\r\n        <mat-chip-list #chipList [selectable]=\"false\">\r\n\r\n            <mat-chip *ngFor=\"let value of values\" [removable]=\"true\" (removed)=\"removeItem(value)\">\r\n                {{value}} <mat-icon matChipRemove>cancel</mat-icon>\r\n            </mat-chip>\r\n\r\n            <!-- \r\n                input field without autocomplete\r\n            -->\r\n            <input\r\n                #chipInput\r\n                [matChipInputFor]=\"chipList\"\r\n                [matChipInputSeparatorKeyCodes]=\"seperatorKeyCodes\"\r\n                [matChipInputAddOnBlur]=\"true\"\r\n                (matChipInputTokenEnd)=\"addItem($event)\"\r\n                autocomplete=\"off\">\r\n\r\n        </mat-chip-list>\r\n    </label>\r\n\r\n    <mat-chip-list #itemList *ngIf=\"available.length\" class=\"available-items\">\r\n        <mat-chip *ngFor=\"let item of available\" (click)=\"selectItem(item)\">\r\n            {{item}}\r\n        </mat-chip>\r\n    </mat-chip-list>\r\n</ng-container>\r\n\r\n<!--\r\n    pills selector with autocomplete strategy, we have to add matAutocompleteTrigger to the input field\r\n    and yes it is possible to do it dynamically. But then we require an instance of MatAutocompleteTrigger\r\n    and this one requires 10 constructor parameters, this is why this exists again here.\r\n-->\r\n<ng-container *ngIf=\"itemSelectStrategy==='autocomplete'\">\r\n    <label class=\"item-form-control\" matAutocompleteOrigin #origin=\"matAutocompleteOrigin\">\r\n        <mat-chip-list #chipList [selectable]=\"false\">\r\n\r\n            <mat-chip *ngFor=\"let value of values\" [removable]=\"true\" (removed)=\"removeItem(value)\">\r\n                {{value}} <mat-icon matChipRemove>cancel</mat-icon>\r\n            </mat-chip>\r\n\r\n            <!-- \r\n                input field without autocomplete\r\n            -->\r\n            <input\r\n                #chipInput\r\n                [formControl]=\"chipInputCtrl\"\r\n                [matAutocomplete]=\"itemAutocomplete\"\r\n                [matAutocompleteConnectedTo]=\"origin\"\r\n                [matChipInputFor]=\"chipList\"\r\n                [matChipInputSeparatorKeyCodes]=\"seperatorKeyCodes\"\r\n                (matChipInputTokenEnd)=\"addItem($event)\"\r\n                autocomplete=\"off\">\r\n        </mat-chip-list>\r\n    </label>\r\n\r\n    <mat-autocomplete #itemAutocomplete=\"matAutocomplete\" (optionSelected)=\"selectItem($event.option.viewValue)\">\r\n        <mat-option *ngFor=\"let item of filteredItems | async\" [value]=\"item\">\r\n            <span>{{item}}</span>\r\n        </mat-option>\r\n    </mat-autocomplete>\r\n</ng-container>\r\n", styles: [":host{display:block;margin-bottom:3rem}:host .item-form-control{border:1px solid #e9e9e9;border-radius:2px;padding:13px 18px;width:100%;display:block;box-shadow:0 5px 5px #e4e4e6}:host ::ng-deep .mat-chip-list-wrapper input{border:0;outline:none}:host ::ng-deep .mat-chip-list-wrapper .mat-chip{background:#86bc25;color:#fff}:host ::ng-deep .mat-chip-list-wrapper .mat-chip-remove{color:#fff;opacity:1}:host .available-items{display:block;margin-top:1.5rem}:host .available-items ::ng-deep .mat-chip{background:white;border:1px solid #86bc25;color:#86bc25}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.FormBuilder }]; }, propDecorators: { autocomplete: [{
                type: ViewChild,
                args: [MatAutocomplete]
            }], autocompleteTrigger: [{
                type: ViewChild,
                args: [MatAutocompleteTrigger]
            }], chipInput: [{
                type: ViewChild,
                args: ['chipInput']
            }], available: [{
                type: Input
            }], values: [{
                type: Input
            }], itemSelectStrategy: [{
                type: Input
            }], itemsChanged: [{
                type: Output
            }] } });

class AihubPickerModule {
}
AihubPickerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AihubPickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AihubPickerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AihubPickerModule, declarations: [AiHubPickerComponent], imports: [CommonModule,
        MatChipsModule,
        MatIconModule,
        MatAutocompleteModule,
        ReactiveFormsModule], exports: [AiHubPickerComponent] });
AihubPickerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AihubPickerModule, providers: [], imports: [[
            CommonModule,
            MatChipsModule,
            MatIconModule,
            MatAutocompleteModule,
            ReactiveFormsModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AihubPickerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AiHubPickerComponent
                    ],
                    imports: [
                        CommonModule,
                        MatChipsModule,
                        MatIconModule,
                        MatAutocompleteModule,
                        ReactiveFormsModule
                    ],
                    exports: [
                        AiHubPickerComponent
                    ],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AiHubPickerComponent, AihubPickerModule };
